import React, { useRef, useState } from "react";
import { Field, Form, Formik } from "formik";
import Select from "react-select";
import SimpleReactValidator from "simple-react-validator";
import {
    VendorFileSvg,
    VendorFolderSvg,
    VendorInfoSvg,
    VendorTrashSvg,
} from "../../../../components/CommonSvg";
import { __t } from "../../../../i18n/translator";
import { SELECT_PROPS, fileKind, optionOf, statusSuffix } from "../ticketUtils";

const MAX_FILES = 5;
/* the cap is on the whole batch, not per file - what the request has to fit
   inside is the upload as a whole */
const MAX_TOTAL_MB = 20;
const MAX_COMMENT = 1000;

const UploadSvg = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M14 3H7.5C6.4 3 5.5 3.9 5.5 5V19C5.5 20.1 6.4 21 7.5 21H16.5C17.6 21 18.5 20.1 18.5 19V7.5L14 3Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
        <path d="M13.75 3.5V7.5H17.75" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path
            d="M12 16.5V11M12 11L10 13M12 11L14 13"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const EMAIL_PATTERN = /^[^s@]+@[^s@]+.[^s@]+$/;

/* one field can hold several addresses - commas or semicolons between them,
   the same way a mail client takes them */
const emailList = (value) =>
    String(value || "")
        .split(/[,;]/)
        .map((email) => email.trim())
        .filter(Boolean);

const badEmails = (value) =>
    emailList(value).filter((email) => !EMAIL_PATTERN.test(email));

const ACCEPTED =
    ".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif,.zip";

const fileSize = (bytes) => {
    const kb = Math.max(1, Math.round((bytes || 0) / 1024));
    return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
};

/* two files picked in separate goes can still be the same file - name and size
   together are enough to spot that without reading the contents */
const sameFile = (one, two) => one.name === two.name && one.size === two.size;

/* the dialog frame comes from CustomisedModal at the call site - this is only
   what goes inside it */
const StatusChangeModal = ({
    ticket,
    toStatus,
    statusOptions,
    saving = false,
    onCancel,
    onSubmit,
}) => {
    const validator = useRef(new SimpleReactValidator());
    const fileInput = useRef(null);
    const [files, setFiles] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [fileError, setFileError] = useState("");
    const [mailError, setMailError] = useState("");
    const [statusError, setStatusError] = useState("");
    /* the status the header pill picked (or, on a plain Reply, the ticket's own
       current one) is only where this starts - the dropdown here is what
       decides what actually gets sent */
    const [statusValue, setStatusValue] = useState(toStatus || ticket?.status || "");

    const changingStatus = !!statusValue && statusValue !== ticket?.status;
    const currentStatusOption =
        optionOf(statusOptions, statusValue) ||
        (statusValue ? { value: statusValue, label: statusValue } : null);

    /* the system half of the update - the comment is what the user adds to it */
    const summary = changingStatus
        ? `Status changed to ${statusValue}`
        : `Reply on #${ticket?.no || ""}`;

    const takeFiles = (picked) => {
        const incoming = Array.from(picked || []);
        if (!incoming.length) return;

        let error = "";
        setFiles((current) => {
            const kept = [...current];
            incoming.forEach((file) => {
                if (kept.length >= MAX_FILES) {
                    error = `You can attach up to ${MAX_FILES} files`;
                    return;
                }
                const total = kept.reduce((sum, held) => sum + held.size, 0);
                if (total + file.size > MAX_TOTAL_MB * 1024 * 1024) {
                    error = `Attachments cannot add up to more than ${MAX_TOTAL_MB} MB`;
                    return;
                }
                if (kept.some((held) => sameFile(held, file))) {
                    error = `${file.name} is already attached`;
                    return;
                }
                kept.push(file);
            });
            return kept;
        });
        setFileError(error);
    };

    const removeFile = (target) => {
        setFileError("");
        setFiles((current) => current.filter((file) => !sameFile(file, target)));
    };

    const handleSubmit = (values) => {
        if (!validator.current.allValid()) {
            validator.current.showMessages();
            return;
        }

        if (!statusValue) {
            setStatusError("Pick a status");
            return;
        }
        setStatusError("");

        const cc = emailList(values.cc);
        const bcc = emailList(values.bcc);
        const invalid = [...badEmails(values.cc), ...badEmails(values.bcc)];
        if (invalid.length > 0) {
            setMailError(
                invalid.length === 1
                    ? `"${invalid[0]}" is not a valid email`
                    : `${invalid.length} of the addresses are not valid emails`
            );
            return;
        }
        setMailError("");

        const formData = new FormData();
        formData.append("ticketId", ticket?.id ?? "");
        formData.append("ticketNo", ticket?.no ?? "");
        formData.append("fromStatus", ticket?.status || "");
        formData.append("status", statusValue || "");
        formData.append("summary", summary);
        formData.append("comment", values.comment.trim());
        formData.append("cc", cc.join(","));
        formData.append("bcc", bcc.join(","));
        files.forEach((file) => formData.append("attachments", file, file.name));

        onSubmit(formData);
    };

    return (
        <div className="vp-st-modal">
            {/* the status is a choice made right here, not something handed in
                already decided - a Reply that leaves it on the ticket's own
                status is exactly as valid as one that moves it on */}
            <div className="vp-st-field vp-st-status-field">
                <label className="vp-st-flabel" htmlFor="vp-st-status">
                    Status<span className="req">*</span>
                </label>
                <Select
                    {...SELECT_PROPS}
                    inputId="vp-st-status"
                    className={`vp-tk-stsel st-${statusSuffix(statusValue)}`}
                    classNamePrefix="vp-tk-stsel"
                    options={statusOptions}
                    value={currentStatusOption}
                    onChange={(option) => setStatusValue(option?.value || "")}
                    isClearable={false}
                    isSearchable={false}
                    placeholder="Set status"
                />
                {statusError ? (
                    <div className="vp-st-info err">
                        <VendorInfoSvg size={16} />
                        {statusError}
                    </div>
                ) : null}
            </div>

            {changingStatus && (
                <div className={`vp-st-note st-${statusSuffix(statusValue)}`}>
                    <VendorInfoSvg size={19} />
                    <span>
                        Status changed to <b>{statusValue}</b>
                    </span>
                </div>
            )}

            <Formik
                initialValues={{ summary: summary, comment: "", cc: "", bcc: "" }}
                onSubmit={handleSubmit}
            >
                {({ values }) => (
                    <Form className="vp-st-form">
                        <div className="vp-st-field">
                            <label className="vp-st-flabel" htmlFor="vp-st-comment">
                                Comment<span className="req">*</span>
                            </label>
                            <div className="vp-st-cwrap">
                                <Field
                                    as="textarea"
                                    id="vp-st-comment"
                                    name="comment"
                                    rows={3}
                                    maxLength={MAX_COMMENT}
                                    placeholder="Enter your comment here..."
                                />
                                <span className="vp-st-count">
                                    {values.comment.length} / {MAX_COMMENT}
                                </span>
                            </div>
                            {validator.current.message(
                                "comment",
                                values.comment,
                                "required",
                                {
                                    messages: {
                                        required: __t("validations.ticket.comment"),
                                    },
                                }
                            )}
                        </div>

                        <label className="vp-st-flabel" htmlFor="vp-st-files">
                            Upload attachment
                        </label>
                        {/* the whole panel is the file input - the button on the
                            right is there to be aimed at, not to be the only way
                            in, and a drop anywhere on it works too */}
                        <label
                            className={`vp-st-attach${dragging ? " drag" : ""}`}
                            onDragEnter={(event) => {
                                event.preventDefault();
                                setDragging(true);
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setDragging(true);
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(event) => {
                                event.preventDefault();
                                setDragging(false);
                                takeFiles(event.dataTransfer.files);
                            }}
                        >
                            <span className="vp-st-aclip">
                                <UploadSvg />
                            </span>
                            <span className="vp-st-abody">
                                <b>Drag &amp; drop files here</b>
                                <i>or click to browse from your device</i>
                                <em>
                                    Max {MAX_FILES} files &bull; {MAX_TOTAL_MB} MB in
                                    total
                                </em>
                            </span>
                            <span className="vp-st-browse">
                                <VendorFolderSvg size={16} />
                                Browse Files
                            </span>
                            <input
                                ref={fileInput}
                                id="vp-st-files"
                                type="file"
                                multiple
                                accept={ACCEPTED}
                                onChange={(event) => {
                                    takeFiles(event.target.files);
                                    /* clearing lets the same file be picked
                                       again after it was removed */
                                    event.target.value = "";
                                }}
                            />
                        </label>

                        {files.length > 0 && (
                            <>
                                <div className="vp-st-fhead">
                                    Attachments ({files.length})
                                </div>
                                {/* cards across the row rather than a stack -
                                    five of them down the page pushed the footer
                                    out of the dialog and put the whole form
                                    behind a scrollbar */}
                                <ul className="vp-st-files">
                                    {files.map((file) => {
                                        const kind = fileKind(file.name);
                                        return (
                                            <li
                                                className="vp-st-file"
                                                key={`${file.name}-${file.size}`}
                                            >
                                                <span className="vp-st-ftop">
                                                    <span
                                                        className={`vp-st-fico ${kind.cls}`}
                                                    >
                                                        <VendorFileSvg size={34} />
                                                        <i>{kind.label}</i>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="vp-st-fx"
                                                        onClick={() => removeFile(file)}
                                                        title={`Remove ${file.name}`}
                                                    >
                                                        <VendorTrashSvg size={15} />
                                                    </button>
                                                </span>
                                                <b
                                                    className="vp-st-fname"
                                                    title={file.name}
                                                >
                                                    {file.name}
                                                </b>
                                                <i className="vp-st-fmeta">
                                                    {kind.label} · {fileSize(file.size)}
                                                </i>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        )}

                        {/* who else gets these details - the mail goes to the
                            store owner and the desk either way, this is anyone
                            on top of that */}
                        <div className="vp-st-share">
                            <b className="vp-st-stitle">Additional recipients</b>
                          
                            <div className="vp-st-srow">
                                <div className="vp-st-field">
                                    <label className="vp-st-flabel" htmlFor="vp-st-cc">
                                        CC
                                    </label>
                                    <Field
                                        id="vp-st-cc"
                                        name="cc"
                                        type="text"
                                        placeholder="name@company.com, another@company.com"
                                    />
                                    <span className="vp-st-shint">
                                        Separate multiple emails with comma
                                    </span>
                                </div>
                                <div className="vp-st-field">
                                    <label className="vp-st-flabel" htmlFor="vp-st-bcc">
                                        BCC
                                    </label>
                                    <Field
                                        id="vp-st-bcc"
                                        name="bcc"
                                        type="text"
                                        placeholder="name@company.com, another@company.com"
                                    />
                                    <span className="vp-st-shint">
                                        Separate multiple emails with comma
                                    </span>
                                </div>
                            </div>

                            {mailError ? (
                                <div className="vp-st-info err">
                                    <VendorInfoSvg size={16} />
                                    {mailError}
                                </div>
                            ) : null}
                        </div>

                        {/* the limits are already written on the drop zone, so
                            this slot only speaks when one of them is broken */}
                        {fileError ? (
                            <div className="vp-st-info err">
                                <VendorInfoSvg size={16} />
                                {fileError}
                            </div>
                        ) : null}

                        <div className="vp-st-foot">
                            <button
                                type="button"
                                className="vp-st-cancel"
                                onClick={onCancel}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="vp-st-submit"
                                disabled={saving}
                            >
                                {saving
                                    ? "Sending…"
                                    : changingStatus
                                    ? "Update Status"
                                    : "Send Reply"}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default StatusChangeModal;
